import { useWindowSize } from '@uidotdev/usehooks'
import type { ChartData, ChartOptions, Plugin } from 'chart.js'
import { BarController, BarElement, Chart as ChartJS } from 'chart.js'
import { Bar } from 'react-chartjs-2'

import { useIsDarkTheme } from '@/utils/useTheme'

import { EmptyChartData } from './empty-chart-data.tsx'
import type { ToggleChartOptions } from './types'
import {
  ChartFonts,
  customToolTip,
  dateLabels,
  getAxisCurrency,
  getAxisFixedDecimal,
  getTooltipCurrency,
  gridLineColor,
} from './utils'

type BarChartOptions = ChartOptions<'bar'>
interface BarChartProps {
  data: ChartData<'bar', (number | [number, number] | null)[], unknown>
  options?: BarChartOptions
  toggleOptions?: ToggleChartOptions
  showCents?: boolean
  plugins?: Plugin[]
}

ChartJS.register(BarController, BarElement)

/**
 * BarChart Component
 *
 * @example
 *
 * ```jsx
 * const data: ChartData<"Bar"> = {
 *   labels: ["Jan", "Feb", "Mar"],
 *   datasets: [
 *     {
 *       label: "Dataset 1",
 *       data: [12, 19, 3],
 *       borderColor: "blue",
 *     },
 *   ],
 * };
 *
 * <BarChart data={data} />
 * ```
 *
 * @param options Additional options for the chart.
 * @param data The chart data.
 */
export const BarChart = ({
  options,
  data,
  toggleOptions,
  showCents,
  plugins = [],
}: BarChartProps) => {
  const isDark = useIsDarkTheme()
  const { width } = useWindowSize()

  const isMobile = (width || 0) < 640

  const chartOptions: BarChartOptions = {
    ...options,
    responsive: true,
    plugins: {
      datalabels: {
        ...options?.plugins?.datalabels,
      },
      legend: {
        display: false,
        ...options?.plugins?.legend,
        labels: {
          font: ChartFonts(isDark, isMobile).bodyFont,
          color: ChartFonts(isDark).color,
          pointStyle: 'circle',
          usePointStyle: true,
          boxWidth: 20,
          boxHeight: 6,
          ...options?.plugins?.legend?.labels,
        },
      },
      tooltip: {
        ...customToolTip,
        ...(toggleOptions?.isTooltipCurrency && getTooltipCurrency(showCents)),
        ...options?.plugins?.tooltip,
      },
    },
    scales: {
      x: {
        ...(toggleOptions?.dateLabels &&
          data.labels &&
          dateLabels(data.labels)),
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          font: ChartFonts(isDark, isMobile).bodyFont,
          color: ChartFonts(isDark).color,
          precision: 0,
          autoSkip: true,
          maxTicksLimit: 7,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        // Hide left border
        border: {
          display: false,
        },
        grid: {
          // Background Grid
          color: gridLineColor(isDark).color,
          display: true,
          drawOnChartArea: true,
          lineWidth: 1.5,
          ...options?.scales?.y?.grid,
        },
        ticks: {
          // Y Labels
          display: !toggleOptions?.hideLabels,
          font: ChartFonts(isDark, isMobile).bodyFont,
          color: ChartFonts(isDark).color,
          precision: 0,
          count: 5,
          align: 'end',
          crossAlign: 'far',
          padding: 5,
          ...(toggleOptions?.fixedDecimalPoints != undefined &&
            getAxisFixedDecimal(toggleOptions?.fixedDecimalPoints)),
          ...(toggleOptions?.isTooltipCurrency && getAxisCurrency(showCents)),
        },
      },
    },
  }

  return !data || (data && data.labels?.length === 0) ? (
    <EmptyChartData isFetching={false} />
  ) : (
    <Bar
      options={chartOptions}
      data={data}
      plugins={plugins}
    />
  )
}
